// React Dependencies
import React, { useState, useEffect } from 'react';

// External Dependencies
import _ from 'lodash';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

// Internal Dependencies
import Colours from '../../../../styles/colours';
import ApiService from '../../../../services/apiService';
import Icons from '../../../../components/Common/Icons';
import OverLoading from '../../../../components/Loading';
import { dateStrToFormat } from '../../../../common/convert';
import { GiftType, RedeemStatus, Url } from '../../../../common/constants';

// Components
import Listing from './Listing';
import ClaimModal from './ClaimModal';
import MText from '../../../../components/Common/MText';
import { containerHeaderTextStyle } from '../../../../styles/common';

const useStyles = makeStyles(() => ({
  textYCenter: {
    display:    'flex',
    alignItems: 'center',
  },
}));

function CouponVoucherTracking(props) {
  const classes = useStyles();
  const { pageId, giftId } = props.match.params;
  const [loading, setLoading] = useState(false);
  const [claimModal, setClaimModal] = useState(false);
  const [gift, setGift] = useState({});
  const [orGifts, setOrGifts] = useState([]);
  const [gifts, setGifts] = useState([]);
  const [giftsToClaim, setGiftsToClaim] = useState([]);
  const [claims, setClaims] = useState(false);

  const fetchShopperGift = async () => {
    try {
      setLoading(true);

      const res = await ApiService.trackingShopperGift(pageId, giftId);
      setGift({
        id:          res.id,
        type:        res.type,
        issued:      res.issued,
        redeemed:    res.redeemed,
        claimed:     res.claimed,
        issuableQty: res.issuableQty,
      });

      const shopperGifts = await ApiService.getShopperGiftByGiftId(pageId, giftId);
      const formatedShopperGift = _.map(shopperGifts, (d) => _.assign({
        id:                d.id,
        giftId:            d.giftId,
        shopperId:         d.shopperId,
        claimId:           d.claimId,
        businessStaffId:   d.businessStaffId,
        groundStaffId:     d.groundStaffId,
        issuedBy:          d.issuerGiftId,
        redeemOutletId:    d.redeemOutletId,
        shopperRedeemDate: d.shopperRedeemDate ? dateStrToFormat(d.shopperRedeemDate) : '',
        issueDate:         d.issueDate ? dateStrToFormat(d.issueDate) : '',
        claimDate:         d.claimDateTime ? dateStrToFormat(d.claimDateTime) : '',
        status:            d.status,
      }));

      setOrGifts(formatedShopperGift);
      setGifts(formatedShopperGift);

      const status = _.map(formatedShopperGift, (v) => v.status);
      setClaims(status.includes(RedeemStatus.Redeemed));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const filterShopperGift = (filter) => {
    const filterResult = orGifts.filter((voucher) => Object.entries(filter).every(([key, value]) => voucher[key] === value || String(voucher[key]).includes(String(value))));

    setGifts([...filterResult]);
  };

  const getPercentage = (amount, total) => {
    const result = total === 0 ? 0 : (amount / total) * 100;
    return `${Math.floor(result)}%`;
  };

  useEffect(() => {
    fetchShopperGift();
  }, []);

  return (
    <>
      { loading && <OverLoading /> }
      <div className="container-fluid bg-white ml-2 mt-3 pb-4">
        <div className="row mt-2">
          <div className="col-12 d-flex flex-row justify-content-start align-items-center">
            <Link to={Url.CouponsAndVouchers.replace(':pageId', pageId)} title="Back" className="d-flex align-items-center">
              <Icons.ArrowBack fontSize="1.8rem" />
            </Link>
            <MText
              className="ml-3"
              text={`${gift.type === GiftType.Voucher ? 'Voucher' : 'Coupon'} Tracking`}
              size={containerHeaderTextStyle.fontSize}
              bold
            />
          </div>
        </div>
        {
          !_.isEmpty(gift) && (
            <>
              <div className="row mt-3">
                <div className="col-12">
                  <MText text={`${gift.type === GiftType.Voucher ? 'Voucher' : 'Coupon'} Program ID: ${gift.id}`} colour={Colours.Gray1} />
                </div>
              </div>

              <div className="row mt-2 mb-4">
                <div className="col-6 col-md-3 col-lg-2">
                  <MText size={13} text="Issuable Quantity" colour={Colours.Gray} />
                  <MText size={13} text={gift.issuableQty || 'No limit'} bold />
                </div>
                <div className="col-6 col-md-3 col-lg-2">
                  <MText size={13} text="Issued" colour={Colours.Gray} />
                  <MText size={13} text={`${gift.issued} (${getPercentage(gift.issued, gift.issuableQty)})`} bold />
                </div>
                <div className="col-6 col-md-3 col-lg-2">
                  <MText size={13} text="Redeemed" colour={Colours.Gray} />
                  <MText size={13} text={`${gift.redeemed} (${getPercentage(gift.redeemed, gift.issuableQty)})`} bold />
                </div>

                {/** To uncomment when the Claim feature is scheduled for launch to merchant */}
                {/* <div className="col-6 col-md-3 col-lg-2">
                  <MText size={13} text="Claimed" />
                  <MText size={13} text={`${gift.claimed} (${getPercentage(gift.claimed, gift.issuableQty)})`} bold />
                </div> */}
              </div>

              {/** TODO: this should be done using MaterialTable */}
              <Listing
                data={gifts}
                gift={gift}
                type={gift.type}
                setGiftsToClaim={(ids) => setGiftsToClaim(ids)}
                claims={claims}
                filterShopperGift={filterShopperGift}
              />

              <div className="container-fluid">
                <div className="row">
                  <div className="col-md-4 col-sm-12">
                    <MText className={classes.textYCenter} text={`Total: ${gift.issued}`} />
                  </div>
                  {/** To uncomment when the Claim feature is scheduled for launch to merchant */}
                  {/* <div className="col-md-8 col-sm-12">
                    {
                      (claims && !IsEmpty(giftsToClaim)) && (
                        <div className="d-flex float-right">
                          <MText
                            className={`${classes.textYCenter} mr-3`}
                            text={`Select multiple redeemed ${gift.type === GiftType.Voucher ? 'voucher' : 'coupon'} to process them together`}
                          />
                          <Button variant="contained" color="primary" onClick={() => setClaimModal(true)}>Process Claims</Button>
                        </div>
                      )
                    }
                  </div> */}
                </div>
              </div>
            </>
          )
        }
        <ClaimModal
          open={claimModal}
          onClose={() => {
            fetchShopperGift();
            setClaimModal(false);
          }}
          gift={gift}
          giftsToClaim={giftsToClaim}
          pageId={pageId}
        />
      </div>
    </>
  );
}

CouponVoucherTracking.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      pageId: PropTypes.string,
      giftId: PropTypes.string,
    }),
  }).isRequired,
};

export default CouponVoucherTracking;
